import { standardizeString } from "../../standardizeString.ts";
import type { ConditionEvaluator } from "../../types/conditionEvaluators.ts";
import type {
  IStringArrayBinaryCondition,
  IStringArrayTernaryCondition,
  StringArrayCondition,
} from "../../types/conditions/arrays/strings.ts";
import { Operator } from "../../types/operators.ts";
import { createThrowConditionNotImplementedForEvaluator, getArrayValues, getSingleValue } from "../common.ts";
import { conditionEvaluator as stringConditionEvaluator } from "../stringConditionEvaluator.ts";

const type = "string";
const operator = Operator.Matches;

const throwNotImplemented = createThrowConditionNotImplementedForEvaluator("string[]");

const binaryConditionEvaluator: ConditionEvaluator<IStringArrayBinaryCondition> = (condition, resolve): boolean => {
  const subjectValues = getArrayValues("string", condition.subject, resolve).map(standardizeString);
  const objectValue = standardizeString(getSingleValue("string", condition.object, resolve));

  switch (condition.operator) {
    case Operator.Has:
      return subjectValues.includes(objectValue);

    case Operator.NotHas:
      return !subjectValues.includes(objectValue);

    case Operator.First:
      return subjectValues.indexOf(objectValue) === 0;

    case Operator.Last:
      return subjectValues.indexOf(objectValue) === subjectValues.length - 1;

    case Operator.Contains:
      return !!subjectValues.find((v) => v.includes(objectValue));

    case Operator.NotContains:
      return !subjectValues.find((v) => v.includes(objectValue));

    default:
      return throwNotImplemented(condition);
  }
};

const binaryMatchesConditionEvaluator: ConditionEvaluator<IStringArrayBinaryCondition> = (
  condition,
  resolve,
): boolean => {
  const subjectValues = getArrayValues("string", condition.subject, resolve);
  const object = getSingleValue("string", condition.object, resolve);

  const matches = subjectValues.map((subject) =>
    stringConditionEvaluator({ type, operator, subject, object }, resolve),
  );

  switch (condition.operator) {
    case Operator.Matches:
      return matches.includes(true);

    case Operator.NotMatches:
      return !matches.includes(true);

    case Operator.MatchesFirst:
      return matches.indexOf(true) === 0;

    case Operator.MatchesLast:
      return matches.lastIndexOf(true) === matches.length - 1;

    default:
      return throwNotImplemented(condition);
  }
};

const ternaryConditionEvaluator: ConditionEvaluator<IStringArrayTernaryCondition> = (condition, resolve): boolean => {
  const subjectValues = getArrayValues("string", condition.subject, resolve).map(standardizeString);
  const left = standardizeString(getSingleValue("string", condition.left, resolve));
  const right = standardizeString(getSingleValue("string", condition.right, resolve));

  const indexLeft = subjectValues.indexOf(left);
  const indexRight = subjectValues.indexOf(right);

  switch (condition.operator) {
    case Operator.Before:
      if (indexLeft === -1 || indexRight === -1) return false;

      return indexLeft < indexRight;

    case Operator.After:
      if (indexLeft === -1 || indexRight === -1) return false;

      return indexLeft > indexRight;

    default:
      return throwNotImplemented(condition);
  }
};

const ternaryMatchesConditionEvaluator: ConditionEvaluator<IStringArrayTernaryCondition> = (
  condition,
  resolve,
): boolean => {
  const left = getSingleValue("string", condition.left, resolve);
  const right = getSingleValue("string", condition.right, resolve);

  const subjectValues = getArrayValues("string", condition.subject, resolve);

  const leftIndex = subjectValues
    .map((subject) => stringConditionEvaluator({ type, operator, subject, object: left }, resolve))
    .indexOf(true);

  const rightIndex = subjectValues
    .map((subject) => stringConditionEvaluator({ type, operator, subject, object: right }, resolve))
    .indexOf(true);

  switch (condition.operator) {
    case Operator.MatchesBefore:
      if (leftIndex === -1 || rightIndex === -1) return false;

      return leftIndex < rightIndex;

    case Operator.MatchesAfter:
      if (leftIndex === -1 || rightIndex === -1) return false;

      return leftIndex > rightIndex;

    default:
      return throwNotImplemented(condition);
  }
};

export const conditionEvaluator: ConditionEvaluator<StringArrayCondition> = (condition, resolve): boolean => {
  switch (condition.operator) {
    case Operator.Has:
    case Operator.NotHas:
    case Operator.First:
    case Operator.Last:
    case Operator.Contains:
    case Operator.NotContains:
      return binaryConditionEvaluator(condition, resolve);

    case Operator.Matches:
    case Operator.NotMatches:
    case Operator.MatchesFirst:
    case Operator.MatchesLast:
      return binaryMatchesConditionEvaluator(condition, resolve);

    case Operator.Before:
    case Operator.After:
      return ternaryConditionEvaluator(condition, resolve);

    case Operator.MatchesBefore:
    case Operator.MatchesAfter:
      return ternaryMatchesConditionEvaluator(condition, resolve);

    default:
      return throwNotImplemented(condition);
  }
};

export default conditionEvaluator;
